import { getIn } from 'formik';

/**
 * Chart values set when installing or reconfiguring an add-on. Fixed, not derived
 * from values.yaml: an add-on's own settings live in Portainer's config store.
 */
export interface AddonValues {
  /** Container image repository; empty uses the chart's default repository. */
  imageRepository: string;
  /** Image tag; empty uses the chart's appVersion. */
  imageTag: string;
  /** StorageClass for the add-on's volume; empty uses the cluster default. */
  storageClass: string;
}

/** Paths of each field in the chart's values, and so in the Helm payload. */
const VALUE_PATHS: Record<keyof AddonValues, string> = {
  imageRepository: 'image.repository',
  imageTag: 'image.tag',
  storageClass: 'storageClass',
};

export function emptyAddonValues(): AddonValues {
  return { imageRepository: '', imageTag: '', storageClass: '' };
}

/**
 * Reads the form's fields out of a release's values. Seeding matters beyond
 * display: a save sends the complete desired state (Helm reset-values), so a
 * blank field would revert to the chart default.
 */
export function toAddonValues(
  releaseValues: Record<string, unknown> | undefined
): AddonValues {
  if (!releaseValues) {
    return emptyAddonValues();
  }

  return {
    imageRepository: readString(releaseValues, VALUE_PATHS.imageRepository),
    imageTag: readString(releaseValues, VALUE_PATHS.imageTag),
    storageClass: readString(releaseValues, VALUE_PATHS.storageClass),
  };
}

/**
 * Builds the nested Helm values payload. Empty fields are dropped so they fall back
 * to the chart default; values are trimmed so a pasted space cannot break it.
 */
export function toHelmValues(values: AddonValues): Record<string, unknown> {
  const image = {
    ...withValue('repository', values.imageRepository),
    ...withValue('tag', values.imageTag),
  };

  return {
    ...(Object.keys(image).length > 0 ? { image } : {}),
    ...withValue('storageClass', values.storageClass),
  };
}

/** True when any field holds a value, i.e. something overrides a default. */
export function hasAnyValue(values: AddonValues): boolean {
  return (Object.keys(values) as Array<keyof AddonValues>).some(
    (key) => values[key].trim() !== ''
  );
}

/** True when any field differs from the baseline the form was seeded with. */
export function hasValueChanges(
  values: AddonValues,
  baseline: AddonValues
): boolean {
  return (Object.keys(values) as Array<keyof AddonValues>).some(
    (key) => values[key].trim() !== baseline[key].trim()
  );
}

function withValue(key: string, value: string): Record<string, string> {
  const trimmed = value.trim();
  return trimmed ? { [key]: trimmed } : {};
}

// Release values parse to real types (a numeric tag becomes a number), so reads
// are stringified for the text inputs.
function readString(values: Record<string, unknown>, path: string): string {
  const value = getIn(values, path);
  if (value === null || value === undefined) {
    return '';
  }
  return typeof value === 'string' ? value : String(value);
}
